<template>
  <v-app>
    <HeaderDealerDashboard 
      :userName="userName" 
      :businessName="businessName"
      @navigate="handleNavigation"
    />
    
    <v-main class="dealer-main">
      <v-container fluid class="pa-6">
        <!-- Page Header -->
        <div class="page-header mb-6">
          <h1 class="text-h4 font-weight-bold text-white mb-0" style="background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%); padding: 20px; border-radius: 8px;">
            <span class="material-icons-round mr-3" style="vertical-align: middle;">inventory</span>
            Dealer Portal - Inventory
          </h1>
        </div>

        <!-- Sync Method Section -->
        <v-card class="mb-6" elevation="3">
          <v-card-title class="text-h5 font-weight-bold primary--text d-flex justify-space-between align-center">
            <div>
              <span class="material-icons-round mr-2">sync</span>
              Sync Method
            </div>
            <v-btn 
              color="primary" 
              outlined 
              @click="forceSync"
              :loading="syncLoading"
              class="text-capitalize"
            >
              <span class="material-icons-round mr-2">refresh</span>
              Force Sync
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-6">
            <div class="sync-methods">
              <v-radio-group v-model="syncMethod" @change="updateSyncMethod">
                <v-radio 
                  label="API Sync — DealerCenter connected (OK)" 
                  value="api"
                  :disabled="!apiConnected"
                ></v-radio>
                <v-radio 
                  label="Website Scraping — Active (Last sync: 2h ago)" 
                  value="scraping"
                ></v-radio>
                <v-radio 
                  label="Manual Upload — CSV/Excel" 
                  value="manual"
                ></v-radio>
              </v-radio-group>
            </div>
          </v-card-text>
        </v-card>

        <!-- Live Inventory Snapshot Section -->
        <v-card class="mb-6" elevation="3">
          <v-card-title class="text-h5 font-weight-bold primary--text d-flex justify-space-between align-center">
            <div>
              <span class="material-icons-round mr-2">inventory_2</span>
              Live Inventory Snapshot
            </div>
            <v-btn 
              color="primary" 
              outlined 
              @click="viewAllVehicles"
              class="text-capitalize"
            >
              <span class="material-icons-round mr-2">visibility</span>
              View All Vehicles
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-6">
            <div class="inventory-stats">
              <v-row>
                <v-col cols="12" md="6">
                  <div class="stat-item mb-3">
                    <strong>Sedans:</strong> {{ inventoryStats.sedans }} | 
                    <strong>SUVs:</strong> {{ inventoryStats.suvs }} | 
                    <strong>Trucks:</strong> {{ inventoryStats.trucks }} | 
                    <strong>Other:</strong> {{ inventoryStats.other }}
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="stat-item mb-3">
                    <strong>Avg Price:</strong> ${{ inventoryStats.avgPrice.toLocaleString() }} | 
                    <strong>Median Miles:</strong> {{ inventoryStats.medianMiles.toLocaleString() }}
                  </div>
                </v-col>
              </v-row>
            </div>
          </v-card-text>
        </v-card>

        <!-- Quality Checks Section -->
        <v-card elevation="3">
          <v-card-title class="text-h5 font-weight-bold primary--text d-flex justify-space-between align-center">
            <div>
              <span class="material-icons-round mr-2">fact_check</span>
              Quality Checks
            </div>
            <v-btn 
              color="orange" 
              outlined 
              @click="fixIssues"
              :loading="fixingIssues"
              class="text-capitalize"
            >
              <span class="material-icons-round mr-2">build</span>
              Fix Issues
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-6">
            <div class="quality-issues">
              <div class="issue-item mb-3" v-if="qualityIssues.missingPhotos > 0">
                <span class="material-icons-round mr-2 text-orange">warning</span>
                <strong>{{ qualityIssues.missingPhotos }} listings missing photos</strong>
              </div>
              <div class="issue-item mb-3" v-if="qualityIssues.missingVin > 0">
                <span class="material-icons-round mr-2 text-orange">warning</span>
                <strong>{{ qualityIssues.missingVin }} listings missing VIN</strong>
              </div>
              <div class="issue-item mb-3" v-if="qualityIssues.duplicates > 0">
                <span class="material-icons-round mr-2 text-red">error</span>
                <strong>{{ qualityIssues.duplicates }} duplicate detected</strong>
              </div>
              <div v-if="qualityIssues.missingPhotos === 0 && qualityIssues.missingVin === 0 && qualityIssues.duplicates === 0" class="text-success">
                <span class="material-icons-round mr-2">check_circle</span>
                <strong>No quality issues detected</strong>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-container>
    </v-main>

    <FooterDealer />

    <!-- View All Vehicles Dialog -->
    <v-dialog v-model="showVehiclesDialog" max-width="1200" scrollable>
      <v-card>
        <v-card-title class="text-h6">
          All Vehicles ({{ totalVehicles }})
          <v-spacer></v-spacer>
          <v-btn icon @click="showVehiclesDialog = false">
            <span class="material-icons-round">close</span>
          </v-btn>
        </v-card-title>
        <v-card-text style="height: 600px;">
          <v-data-table
            :headers="vehicleHeaders"
            :items="vehicles"
            :loading="vehiclesLoading"
            :items-per-page="10"
            class="elevation-1"
          >
            <template v-slot:item.price="{ item }">
              ${{ item.price.toLocaleString() }}
            </template>
            <template v-slot:item.mileage="{ item }">
              {{ item.mileage.toLocaleString() }}
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip :color="item.status === 'Available' ? 'success' : 'warning'" small>
                {{ item.status }}
              </v-chip>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Fix Issues Dialog -->
    <v-dialog v-model="showFixIssuesDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6">Fix Quality Issues</v-card-title>
        <v-card-text>
          <p>This will attempt to automatically fix the following issues:</p>
          <ul>
            <li v-if="qualityIssues.missingPhotos > 0">Fetch missing photos from source</li>
            <li v-if="qualityIssues.missingVin > 0">Attempt VIN lookup</li>
            <li v-if="qualityIssues.duplicates > 0">Remove duplicate listings</li>
          </ul>
          <p class="mt-3"><strong>Note:</strong> This process may take several minutes.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="showFixIssuesDialog = false">Cancel</v-btn>
          <v-btn color="orange" @click="startFixIssues">Start Fix</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import HeaderDealerDashboard from '@/components/Layout/HeaderDealerDashboard.vue'
import FooterDealer from '@/components/Layout/FooterDealer.vue'

export default {
  name: 'DealerInventory',
  components: {
    HeaderDealerDashboard,
    FooterDealer
  },
  data() {
    return {
      loading: false,
      syncLoading: false,
      fixingIssues: false,
      vehiclesLoading: false,
      userName: '',
      businessName: '',
      showVehiclesDialog: false,
      showFixIssuesDialog: false,
      syncMethod: 'scraping',
      apiConnected: false,
      inventoryStats: {
        sedans: 72,
        suvs: 68,
        trucks: 28,
        other: 16,
        avgPrice: 24900,
        medianMiles: 41000
      },
      qualityIssues: {
        missingPhotos: 3,
        missingVin: 5,
        duplicates: 1
      },
      totalVehicles: 184,
      vehicles: [],
      vehicleHeaders: [
        { text: 'Year', value: 'year' },
        { text: 'Make', value: 'make' },
        { text: 'Model', value: 'model' },
        { text: 'Price', value: 'price' },
        { text: 'Mileage', value: 'mileage' },
        { text: 'Status', value: 'status' }
      ]
    }
  },
  beforeCreate() {
    // Check authentication
    if (!localStorage.getItem('token') || localStorage.getItem('user_flag') !== 'dealer') {
      this.$router.push('/dealers/login')
    }
  },
  async mounted() {
    await this.loadUserData()
    await this.loadInventoryData()
  },
  methods: {
    loadUserData() {
      this.userName = localStorage.getItem('user_name') || 'Dealer'
      this.businessName = localStorage.getItem('business_name') || 'Business'
    },
    async loadInventoryData() {
      try {
        this.loading = true
        const token = localStorage.getItem('token')
        
        const response = await axios.get('/api/dealer/inventory-data', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.data.flag === 1) {
          this.inventoryStats = response.data.data.stats || this.inventoryStats
          this.qualityIssues = response.data.data.quality_issues || this.qualityIssues
          this.syncMethod = response.data.data.sync_method || this.syncMethod
          this.apiConnected = response.data.data.api_connected || false
          this.totalVehicles = response.data.data.total_vehicles || this.totalVehicles
        }
      } catch (error) {
        console.error('Error loading inventory data:', error)
        this.$toast.error('Error loading inventory data')
      } finally {
        this.loading = false
      }
    },
    async loadVehicles() {
      try {
        this.vehiclesLoading = true
        const token = localStorage.getItem('token')
        
        const response = await axios.get('/api/dealer/vehicles', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.data.flag === 1) {
          this.vehicles = response.data.data.vehicles || []
        }
      } catch (error) {
        console.error('Error loading vehicles:', error)
        this.$toast.error('Error loading vehicles')
      } finally {
        this.vehiclesLoading = false
      }
    },
    handleNavigation(item) {
      // Navigation is handled by the HeaderDealerDashboard component
    },
    async forceSync() {
      try {
        this.syncLoading = true
        const token = localStorage.getItem('token')
        
        const response = await axios.post('/api/dealer/force-sync', {}, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.data.flag === 1) {
          this.$toast.success('Inventory sync started successfully!')
          await this.loadInventoryData()
        } else {
          this.$toast.error(response.data.message || 'Error starting sync')
        }
      } catch (error) {
        console.error('Error forcing sync:', error)
        this.$toast.error('Error starting inventory sync')
      } finally {
        this.syncLoading = false
      }
    },
    async updateSyncMethod() {
      try {
        const token = localStorage.getItem('token')
        
        const response = await axios.post('/api/dealer/update-sync-method', {
          sync_method: this.syncMethod
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.data.flag === 1) {
          this.$toast.success('Sync method updated successfully!')
        } else {
          this.$toast.error(response.data.message || 'Error updating sync method')
        }
      } catch (error) {
        console.error('Error updating sync method:', error)
        this.$toast.error('Error updating sync method')
      }
    },
    async viewAllVehicles() {
      this.showVehiclesDialog = true
      await this.loadVehicles()
    },
    fixIssues() {
      this.showFixIssuesDialog = true
    },
    async startFixIssues() {
      try {
        this.fixingIssues = true
        this.showFixIssuesDialog = false
        const token = localStorage.getItem('token')
        
        const response = await axios.post('/api/dealer/fix-quality-issues', {}, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.data.flag === 1) {
          this.$toast.success('Quality issues fix started! This may take several minutes.')
          // Reload data after a delay
          setTimeout(() => {
            this.loadInventoryData()
          }, 5000)
        } else {
          this.$toast.error(response.data.message || 'Error starting quality fix')
        }
      } catch (error) {
        console.error('Error fixing quality issues:', error)
        this.$toast.error('Error starting quality issues fix')
      } finally {
        this.fixingIssues = false
      }
    }
  }
}
</script>

<style scoped>
.dealer-main {
  background-color: #f5f5f5;
  min-height: 100vh;
}

.page-header h1 {
  text-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.sync-methods .v-radio {
  margin-bottom: 8px;
}

.inventory-stats .stat-item {
  font-size: 16px;
  line-height: 1.6;
}

.quality-issues .issue-item {
  display: flex;
  align-items: center;
  font-size: 16px;
  line-height: 1.6;
}

.text-orange {
  color: #ff9800 !important;
}

.text-red {
  color: #f44336 !important;
}

.text-success {
  color: #4caf50 !important;
  display: flex;
  align-items: center;
  font-size: 16px;
}

.v-card {
  border-radius: 12px !important;
}

.v-card-title {
  padding: 20px 24px 16px 24px;
}

.v-card-text {
  padding: 0 24px 24px 24px;
}
</style>
